import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router';
import { validateToken } from '@/api/auth';
import { acceptInvite } from '@/api/membership';
import ErrorCard from '@/components/UI/ErrorCard';
import Loading from '@/components/UI/loading';
import AccountPortalLayout from '@/layouts/AccountPortalLayout';
import type { AppDispatch } from '../../store';
import { SignOutUser } from '../../store/Auth';
import { sanitizeResponseError } from '../../utils';
import InvitePasswordSetupForm, { type InvitePasswordSetupFormValues } from '../UserOnboard/InvitePasswordSetupForm';

const Invite = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token')?.trim();
  const dispatch = useDispatch<AppDispatch>();
  const [isTokenValid, setTokenValid] = useState(false);
  const [validatingToken, setValidatingToken] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [temporaryPasswordErrorMessage, setTemporaryPasswordErrorMessage] = useState('');
  const { hostname } = window.location;
  const accountName = hostname.split('.')[0];

  useEffect(() => {
    dispatch(SignOutUser());
  }, [dispatch]);

  useEffect(() => {
    const checkTokenValid = async (inviteToken: string) => {
      setValidatingToken(true);
      try {
        const res = await validateToken(inviteToken);
        if (res.valid) {
          setTokenValid(true);
        } else {
          setTokenValid(false);
          setErrorMessage(
            `Uh oh! it seems there is some issues in your invitation: <${res.message}>. Please contact the Administrator of ${accountName}.`
          );
        }
      } catch (error) {
        setTokenValid(false);
        setErrorMessage(sanitizeResponseError(error));
      } finally {
        setValidatingToken(false);
      }
    };

    if (token) {
      checkTokenValid(token);
    } else {
      setErrorMessage(
        `Uh oh! it seems there is some issues in your invitation: <Token not found>. Please contact the Administrator of ${accountName}.`
      );
    }
  }, [token, accountName]);

  const acceptInviteMutation = useMutation({
    mutationFn: (values: InvitePasswordSetupFormValues) => {
      if (!token) {
        throw new Error('Token not found');
      }

      return acceptInvite({
        ...values,
        token,
      });
    },
    onSuccess: () => {
      localStorage.setItem('invite-accepted', 'true');
      dispatch(SignOutUser());
      window.location.href = `${window.location.origin}/auth/sign_in`;
    },
    onError: (error) => {
      const message = sanitizeResponseError(error);
      setTemporaryPasswordErrorMessage(message || 'Incorrect temporary password');
    },
  });

  return (
    <AccountPortalLayout companyName='member onboarding'>
      {validatingToken && <Loading loadingText='Validating' />}
      {!isTokenValid && !validatingToken && (
        <div className='w-full'>
          <ErrorCard errorMsg={errorMessage} errorTitle='Invalid Token' />
        </div>
      )}
      <div className='conatiner mx-auto max-w-lg lg:w-1/2  md:w-8/12  sm:w-9/12 py-10 '>
        {isTokenValid && (
          <InvitePasswordSetupForm
            onSubmit={(values) => acceptInviteMutation.mutate(values)}
            isSubmitting={acceptInviteMutation.isLoading}
            temporaryPasswordErrorMessage={temporaryPasswordErrorMessage}
          />
        )}
      </div>
    </AccountPortalLayout>
  );
};

export default Invite;
